// src/App.tsx

import React, { Suspense, useEffect, lazy } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useParams } from 'react-router-dom';
import Header from './components/Header/Header';
import Footer from './components/Footer/Footer';
import LoadingSpinner from './components/LoadingSpinner/LoadingSpinner';
import ErrorBoundary from './components/ErrorBoundary/ErrorBoundary';
import ScrollToTopOnNavigate from './components/ScrollToTopOnNavigate';
import { OrganizationSchema } from './components/Schema';
// Import ToastContainer lazily to reduce initial bundle size
// import { ToastContainer } from 'react-toastify';
// import 'react-toastify/dist/ReactToastify.css';
import tokenManager from './utils/tokenManager';
import { LazyMotion } from 'framer-motion';

// Lazy load the ToastContainer wrapper
const LazyToast = lazy(() => import('./components/ui/LazyToast'));

// Lazy load the framer motion features bundle
const loadFeatures = () => import('./framer-features').then(res => res.default);

// Preload HomePage removed from direct import for better initial index.js size
// import HomePage from './pages/Home/HomePage';
const HomePage = lazy(() => import('./pages/Home/HomePage'));

// Define the type for components that can be preloaded
type PreloadableComponent = React.LazyExoticComponent<React.ComponentType<any>> & {
    preload?: () => Promise<{ default: React.ComponentType<any> }>;
};

// Custom loader for efficient code splitting
const lazyWithPreload = (factory: () => Promise<{ default: React.ComponentType<any> }>): PreloadableComponent => {
    const Component = lazy(factory) as PreloadableComponent;
    Component.preload = factory;
    return Component;
};

// Other pages with standard lazy loading
const TextCompare = lazy(() => import('./pages/Tools/TextCompare/TextCompare'));
const AboutPage = lazy(() => import('./pages/About/AboutPage'));
const ContactPage = lazy(() => import('./pages/Contact/ContactPage'));
const PrivacyPolicy = lazy(() => import('./pages/Privacy/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/Terms/TermsOfService'));
const FeaturesPage = lazy(() => import('./pages/Features/FeaturesPage'));
const ToolsPage = lazyWithPreload(() => import('./pages/Tools/ToolsPage'));
const DevHubPage = lazy(() => import('./pages/DevHub/DevHubPage'));
const MockApiPage = lazy(() => import('./pages/DevHub/MockApi/MockApiPage'));
const WebhookEchoPage = lazy(() => import('./pages/DevHub/WebhookEcho/WebhookEchoPage'));
const DevMailPage = lazy(() => import('./pages/DevHub/DevMail/DevMailPage'));
const PaymentMockPage = lazy(() => import('./pages/DevHub/PaymentMock/PaymentMockPage'));
const FAQPage = lazy(() => import('./pages/FAQ/FAQPage'));

const FileReducerPage = lazy(() => import('./pages/Tools/FileReducer/FileReducerPage'));
const FileMergeSplitPage = lazy(() => import('./pages/Tools/FileMergeSplit/FileMergeSplitPage'));

const InsightsPage = lazy(() => import('./pages/Insights/InsightsPage'));
const ArticleDetail = lazy(() => import('./pages/Insights/Articles/ArticleDetail'));
const ArticlesPage = lazy(() => import('./pages/Insights/Articles/ArticlesPage'));

const CoursesHub = lazy(() => import('./pages/Insights/Courses/CoursesHub'));
const CourseDetailPage = lazy(() => import('./pages/Insights/Courses/CourseDetailPage'));

const QRToolPage = lazy(() => import('./pages/Tools/QRTool/QRToolPage'));
const BarcodeReaderPage = lazy(() => import('./pages/Tools/BarcodeReader/BarcodeReaderPage'));
const OCRToolPage = lazy(() => import('./pages/Tools/OCRTool/OCRToolPage'));
const BackgroundRemoverPage = lazy(() => import('./pages/Tools/BackgroundRemover/BackgroundRemoverPage'));
const JSONValidatorPage = lazy(() => import('./pages/Tools/JSONValidator/JSONValidator'));

// Simple placeholder for lazy-loaded content
const LazyLoadFallback: React.FC = () => <LoadingSpinner size="medium" />;

// Specialized Page Loading Fallback to prevent Footer Jump (CLS Fix)
const PageLoadingFallback: React.FC = () => (
    <div className="flex items-center justify-center min-h-[80vh] w-full">
        <LoadingSpinner size="medium" />
    </div>
);

// Specialized Skeleton Loader for Courses to prevent CLS
const CourseLoadingFallback: React.FC = () => (
    <div className="course-player-v2 sidebar-active">
        <div className="player-body-v2">
            <aside className="course-sidebar-v2">
                <div className="sidebar-header-v2">
                    <div className="shimmer" style={{ width: '150px', height: '30px', borderRadius: '4px' }} />
                </div>
                <div style={{ padding: '20px' }}>
                    {[1, 2, 3, 4, 5].map(i => (
                        <div key={i} className="shimmer" style={{ width: '100%', height: '40px', marginBottom: '10px', borderRadius: '4px' }} />
                    ))}
                </div>
            </aside>
            <main className="course-main-content">
                <div className="article-wrapper" style={{ padding: '40px' }}>
                    <div className="shimmer" style={{ width: '40%', height: '40px', marginBottom: '20px', borderRadius: '4px' }} />
                    <div className="shimmer" style={{ width: '100%', height: '200px', marginBottom: '20px', borderRadius: '8px' }} />
                    <div className="shimmer" style={{ width: '90%', height: '20px', marginBottom: '10px', borderRadius: '4px' }} />
                    <div className="shimmer" style={{ width: '95%', height: '20px', marginBottom: '10px', borderRadius: '4px' }} />
                </div>
            </main>
        </div>
    </div>
);

// SEO Redirect for articles: /insights/:slug -> /insights/articles/:slug
const ArticleRedirect: React.FC = () => {
    const { slug } = useParams<{ slug: string }>();
    return <Navigate to={`/insights/articles/${slug}`} replace />;
};

// SEO Redirect for tutorials: /insights/tutorials/... -> /insights/courses/...
const TutorialRedirect: React.FC = () => {
    const { language, slug } = useParams<{ language: string; slug?: string }>();
    if (slug && language) {
        return <Navigate to={`/insights/courses/${language}/${slug}`} replace />;
    }
    if (language) {
        return <Navigate to={`/insights/courses/${language}`} replace />;
    }
    return <Navigate to="/insights/courses" replace />;
};

function AppContent() {


    // Set up preloading for frequently accessed routes
    useEffect(() => {
        const setAppHeight = () => {
            document.documentElement.style.minHeight = `${window.innerHeight}px`;
        };

        setAppHeight();
        window.addEventListener('resize', setAppHeight);

        let preloadTimer: NodeJS.Timeout;
        const preloadCommonRoutes = () => {
            if ('requestIdleCallback' in window) {
                (window as any).requestIdleCallback(() => {
                    const routesToPreload = [
                        ToolsPage.preload
                    ];

                    let index = 0;
                    const preloadNextRoute = () => {
                        if (index < routesToPreload.length) {
                            try {
                                // Performance Optimization: Only preload on Desktop/Fast connections
                                // Avoid background fetches on mobile to preserve bandwidth for primary content
                                const isMobile = window.innerWidth < 1024;
                                const preloadFn = routesToPreload[index];
                                if (typeof preloadFn === 'function' && !isMobile) {
                                    preloadFn();
                                }
                            } catch (e) {
                                // Fail silently
                            }
                            index++;
                            preloadTimer = setTimeout(preloadNextRoute, 3000);
                        }
                    };

                    preloadNextRoute();
                }, { timeout: 5000 });
            }
        };

        const loadingTimer = setTimeout(preloadCommonRoutes, 3000);
        tokenManager.getToken();

        return () => {
            window.removeEventListener('resize', setAppHeight);
            clearTimeout(loadingTimer);
            if (preloadTimer) clearTimeout(preloadTimer);
        };
    }, []);

    return (
        <ErrorBoundary>
            <OrganizationSchema />
            <ScrollToTopOnNavigate />

            <Suspense fallback={null}>
                <LazyToast />
            </Suspense>

            <LazyMotion features={loadFeatures} strict>
                <div className="flex flex-col min-h-screen bg-transparent">
                    <Header />

                    <main className="flex-1 mt-[48px] min-h-[80vh]">
                        <Routes>
                            <Route path="/" element={
                                <Suspense fallback={<PageLoadingFallback />}>
                                    <HomePage />
                                </Suspense>
                            } />

                            <Route path="/tools/compare-text" element={
                                <Suspense fallback={<LazyLoadFallback />}>
                                    <TextCompare />
                                </Suspense>
                            } />
                            <Route path="/tools/text-comparison-tool" element={<Navigate to="/tools/compare-text" replace />} />
                            <Route path="/tools/text-compare" element={<Navigate to="/tools/compare-text" replace />} />
                            <Route path="/tools" element={
                                <Suspense fallback={<LazyLoadFallback />}>
                                    <ToolsPage />
                                </Suspense>
                            } />
                            <Route path="/dev-hub" element={
                                <Suspense fallback={<LazyLoadFallback />}>
                                    <DevHubPage />
                                </Suspense>
                            } />
                            <Route path="/dev-hub/mock-api" element={
                                <Suspense fallback={<LazyLoadFallback />}>
                                    <MockApiPage />
                                </Suspense>
                            } />
                            <Route path="/dev-hub/webhook-tester" element={
                                <Suspense fallback={<LazyLoadFallback />}>
                                    <WebhookEchoPage />
                                </Suspense>
                            } />
                            <Route path="/dev-hub/email-sandbox" element={
                                <Suspense fallback={<LazyLoadFallback />}>
                                    <DevMailPage />
                                </Suspense>
                            } />
                            <Route path="/dev-hub/payment-simulator" element={
                                <Suspense fallback={<LazyLoadFallback />}>
                                    <PaymentMockPage />
                                </Suspense>
                            } />
                            <Route path="/tools/compress-pdf-images" element={
                                <Suspense fallback={<LazyLoadFallback />}>
                                    <FileReducerPage />
                                </Suspense>
                            } />
                            <Route path="/tools/merge-split-pdf" element={
                                <Suspense fallback={<LazyLoadFallback />}>
                                    <FileMergeSplitPage />
                                </Suspense>
                            } />
                            <Route path="/tools/qr-code-generator" element={
                                <Suspense fallback={<LazyLoadFallback />}>
                                    <QRToolPage />
                                </Suspense>
                            } />
                            <Route path="/tools/barcode-scanner" element={
                                <Suspense fallback={<LazyLoadFallback />}>
                                    <BarcodeReaderPage />
                                </Suspense>
                            } />
                            <Route path="/tools/scan-image-to-text" element={
                                <Suspense fallback={<LazyLoadFallback />}>
                                    <OCRToolPage />
                                </Suspense>
                            } />
                            <Route path="/tools/remove-background" element={
                                <Suspense fallback={<LazyLoadFallback />}>
                                    <BackgroundRemoverPage />
                                </Suspense>
                            } />
                            <Route path="/tools/json-validator-formatter" element={
                                <Suspense fallback={<LazyLoadFallback />}>
                                    <JSONValidatorPage />
                                </Suspense>
                            } />
                            <Route path="/about" element={
                                <Suspense fallback={<LazyLoadFallback />}>
                                    <AboutPage />
                                </Suspense>
                            } />
                            <Route path="/contact" element={
                                <Suspense fallback={<LazyLoadFallback />}>
                                    <ContactPage />
                                </Suspense>
                            } />
                            <Route path="/privacy" element={
                                <Suspense fallback={<LazyLoadFallback />}>
                                    <PrivacyPolicy />
                                </Suspense>
                            } />
                            <Route path="/terms" element={
                                <Suspense fallback={<LazyLoadFallback />}>
                                    <TermsOfService />
                                </Suspense>
                            } />
                            <Route path="/features" element={
                                <Suspense fallback={<LazyLoadFallback />}>
                                    <FeaturesPage />
                                </Suspense>
                            } />
                            <Route path="/faq" element={
                                <Suspense fallback={<LazyLoadFallback />}>
                                    <FAQPage />
                                </Suspense>
                            } />

                            {/* Insights Routes */}
                            <Route path="/insights/articles" element={
                                <Suspense fallback={<PageLoadingFallback />}>
                                    <ArticlesPage />
                                </Suspense>
                            } />
                            <Route path="/insights/articles/:slug" element={
                                <Suspense fallback={<LazyLoadFallback />}>
                                    <ArticleDetail />
                                </Suspense>
                            } />
                            <Route path="/insights" element={
                                <Suspense fallback={<PageLoadingFallback />}>
                                    <InsightsPage />
                                </Suspense>
                            } />

                            {/* Course Routes - Uses specialized skeleton fallback */}
                            <Route path="/insights/courses" element={
                                <Suspense fallback={<CourseLoadingFallback />}>
                                    <CoursesHub />
                                </Suspense>
                            } />
                            <Route path="/insights/courses/:language" element={
                                <Suspense fallback={<CourseLoadingFallback />}>
                                    <CourseDetailPage />
                                </Suspense>
                            } />
                            <Route path="/insights/courses/:language/:slug" element={
                                <Suspense fallback={<CourseLoadingFallback />}>
                                    <CourseDetailPage />
                                </Suspense>
                            } />

                            {/* Legacy Tutorial Redirects */}
                            <Route path="/insights/tutorials" element={<Navigate to="/insights/courses" replace />} />
                            <Route path="/insights/tutorials/:language" element={<TutorialRedirect />} />
                            <Route path="/insights/tutorials/:language/:slug" element={<TutorialRedirect />} />

                            <Route path="/insights/:slug" element={
                                <ArticleRedirect />
                            } />
                        </Routes>
                    </main>

                    <Footer />
                </div>
            </LazyMotion>
        </ErrorBoundary>
    );
}

function App() {
    return (
        <Router>
            <AppContent />
        </Router>
    );
}

export default App;
